/**
 * What the specialists screens read from hg-order. Every key starts with
 * `SPECIALISTS_KEY`, so a decision about one specialist refreshes the page,
 * the list and the counts on its tabs in one call.
 *
 * The staff stream says when to read them again (`./live.ts`); the screens
 * read with `useLiveQuery`, which also asks by itself as a backstop.
 */
import { keepPreviousData, queryOptions } from "@tanstack/react-query";

import { orderApi, unwrap } from "@/shared/api/clients";
import { useLiveQuery } from "@/shared/live";

import type { SpecialistDetail, SpecialistStatus, VisitStatus } from "./types";

export const SPECIALISTS_KEY = ["order", "specialists"] as const;

/** Rows of a list page. */
export const PER_PAGE = 25;
/** Ratings shown at a time on a specialist's page. */
export const RATINGS_PER_PAGE = 5;
/** Visits shown at a time on a specialist's page. */
export const VISITS_PER_PAGE = 10;

/** The filters `GET /v2/admin/specialists` has. There are no others. */
export interface SpecialistsFilter {
  status?: SpecialistStatus;
  profession_id?: string;
  area_id?: string;
  is_online?: boolean;
  q?: string;
}

export function specialistsQuery(filter: SpecialistsFilter, page: number, perPage = PER_PAGE) {
  return queryOptions({
    queryKey: [...SPECIALISTS_KEY, "list", filter, page, perPage] as const,
    queryFn: ({ signal }) =>
      unwrap(
        orderApi.GET("/v2/admin/specialists", {
          params: { query: { ...filter, page, per_page: perPage } },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/** The list's filters without the status: what the tabs' counts are asked with. */
export type CountsFilter = Omit<SpecialistsFilter, "status">;

/**
 * How many specialists each status holds, with the list's other filters: the
 * numbers on the tabs, and the sidebar's.
 */
export function specialistCountsQuery(filter: CountsFilter = {}) {
  return queryOptions({
    queryKey: [...SPECIALISTS_KEY, "counts", filter] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/specialists/counts", { params: { query: filter }, signal }),
      );
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

export function specialistKey(userId: string) {
  return [...SPECIALISTS_KEY, "one", userId] as const;
}

export function specialistQuery(userId: string) {
  return queryOptions({
    queryKey: specialistKey(userId),
    queryFn: async ({ signal }): Promise<SpecialistDetail> => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/specialists/{user_id}", {
          params: { path: { user_id: userId } },
          signal,
        }),
      );
      return data;
    },
  });
}

export function specialistVisitsKey(userId: string) {
  return [...SPECIALISTS_KEY, "visits", userId] as const;
}

/** The visits a specialist holds or held to their end, the latest time first. */
export function specialistVisitsQuery(
  userId: string,
  status: VisitStatus | undefined,
  page: number,
) {
  return queryOptions({
    queryKey: [...specialistVisitsKey(userId), status ?? null, page] as const,
    queryFn: ({ signal }) =>
      unwrap(
        orderApi.GET("/v2/admin/specialists/{user_id}/visits", {
          params: {
            path: { user_id: userId },
            query: { ...(status === undefined ? {} : { status }), page, per_page: VISITS_PER_PAGE },
          },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/** How a specialist is rated in sum, and the commission tier the ratings have earned. */
export function ratingSummaryQuery(userId: string) {
  return queryOptions({
    queryKey: [...SPECIALISTS_KEY, "rating", userId] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/specialists/{user_id}/rating", {
          params: { path: { user_id: userId } },
          signal,
        }),
      );
      return data;
    },
  });
}

/** Which of a specialist's ratings: only the hidden, only the shown, or both. */
export type RatingsShown = "hidden" | "shown";

/** A specialist's ratings, the newest first: all of them, or only the hidden or the shown. */
export function ratingsQuery(userId: string, page: number, shown?: RatingsShown) {
  return queryOptions({
    queryKey: [...SPECIALISTS_KEY, "ratings", userId, page, shown ?? null] as const,
    queryFn: ({ signal }) =>
      unwrap(
        orderApi.GET("/v2/admin/ratings", {
          params: {
            query: {
              specialist_id: userId,
              ...(shown === undefined ? {} : { hidden: shown === "hidden" }),
              page,
              per_page: RATINGS_PER_PAGE,
            },
          },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/** The filters of `GET /v2/admin/ratings` the queue across every specialist uses. */
export interface RatingsFilter {
  /** Only ratings of this score or lower. */
  max_score?: number;
  /** Only the hidden, or only the shown. */
  hidden?: boolean;
}

/** Ratings across every specialist, the newest first: the low ones, the hidden ones. */
export function allRatingsQuery(filter: RatingsFilter, page: number, perPage = PER_PAGE) {
  return queryOptions({
    queryKey: [...SPECIALISTS_KEY, "ratings", "all", filter, page, perPage] as const,
    queryFn: ({ signal }) =>
      unwrap(
        orderApi.GET("/v2/admin/ratings", {
          params: { query: { ...filter, page, per_page: perPage } },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/** The span the stretches at work are asked for: both sides left out, hg-order's last week. */
export interface OnlineSpan {
  from?: string;
  to?: string;
}

/** The stretches a specialist was ready to work in a span, and how many seconds they add up to. */
export function onlineSessionsQuery(userId: string, span: OnlineSpan) {
  return queryOptions({
    queryKey: [...SPECIALISTS_KEY, "online", userId, span] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/specialists/{user_id}/online-sessions", {
          params: { path: { user_id: userId }, query: span },
          signal,
        }),
      );
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

/** How many specialists wait for a decision: the sidebar's badge. Undefined until known. */
export function useSpecialistsInReviewCount(): number | undefined {
  return useLiveQuery({
    ...specialistCountsQuery(),
    select: (counts) => counts.in_review,
  }).data;
}
